import { X, DollarSign, Database, AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";
import SectionCard from "./SectionCard";
import { formatNum } from "@/lib/constants";
import LeyendaEstado from "./pipeline/LeyendaEstado";
import AreaChart from "./charts/AreaChart";
import BarChart from "./charts/BarChart";

const ETAPA_CONFIG = {
  facturacion: {
    title: "Facturación",
    subtitle: "ciclo_ingresos.ar_camadas_tot_monto",
    color: "bg-violet-500",
  },
  vigente: {
    title: "Cartera Vigente (0-30 días)",
    subtitle: "Cartera dentro de plazo de cobro",
    color: "bg-emerald-500",
  },
  mora_temprana: {
    title: "Mora Temprana (31-60 días)",
    subtitle: "Cartera con atraso leve",
    color: "bg-amber-500",
  },
  mora_avanzada: {
    title: "Mora Avanzada (61-360+ días)",
    subtitle: "Cartera vencida de difícil cobro",
    color: "bg-red-500",
  },
  recuperaciones: {
    title: "Recuperaciones",
    subtitle: "Montos recuperados del período",
    color: "bg-blue-500",
  },
};

export default function CobranzasDrawer({ etapa, data, periodo, onClose }) {
  if (!data || !etapa) return null;

  const cfg = ETAPA_CONFIG[etapa] || ETAPA_CONFIG.facturacion;

  // Evolución mensual para charts
  const evoMeses = (data.evolucion_mensual || []).map((e) => e.mes);
  const evoMonto = (data.evolucion_mensual || []).map((e) => e.monto_mm);
  const evoVigente = (data.evolucion_mensual || []).map((e) => e.vigente_mm);
  const evoMora = (data.evolucion_mensual || []).map((e) => e.mora31_mm);

  // Top convenios para bar chart
  const topConvenios = data.top_convenios || [];
  const convenioLabels = topConvenios.map((c) => c.convenio.length > 20 ? c.convenio.slice(0, 18) + "…" : c.convenio);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cobranzas-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl ${cfg.color} flex items-center justify-center text-white`}>
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{cfg.title}</h3>
                <p className="text-xs text-slate-500">{cfg.subtitle} · {periodo}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
              aria-label="Cerrar panel"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <LeyendaEstado estado={data.origen || "REAL"} />
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Warning banner */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Datos históricos</div>
              <p className="text-sm text-slate-700 mt-1">
                {data.nota_periodo || "Datos de Oct 2025 — ciclo_ingresos no tiene datos 2026 aún"}
              </p>
              <p className="text-xs text-slate-500 mt-1">Ref: {data.mes_referencia}</p>
            </div>
          </div>

          {/* === FACTURACIÓN === */}
          {etapa === "facturacion" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monto total</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">${formatNum(data.monto_total_mm)}</div>
                  <div className="text-sm text-slate-500">MM CLP</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Episodios</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.episodios)}</div>
                  <div className="text-sm text-slate-500">facturados</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">% Vigente</div>
                  <div className="mt-2 text-2xl font-bold text-emerald-600 tabular-nums">{data.pct_vigente}%</div>
                  <div className="text-sm text-slate-500">dentro de plazo</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mora temprana</div>
                  <div className="mt-2 text-2xl font-bold text-amber-600 tabular-nums">{data.pct_mora_temprana}%</div>
                  <div className="text-sm text-slate-500">31-60 días</div>
                </div>
              </div>

              {/* Top convenios */}
              <SectionCard title="Top convenios por monto" subtitle="Principales pagadores">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Convenio</th>
                        <th className="text-right py-2 text-xs font-semibold text-slate-500 uppercase">Monto MM</th>
                        <th className="text-right py-2 text-xs font-semibold text-slate-500 uppercase">Vigente MM</th>
                        <th className="text-right py-2 text-xs font-semibold text-slate-500 uppercase">% Vig</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topConvenios.map((c, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="py-2 text-slate-700 text-xs">{c.convenio}</td>
                          <td className="py-2 text-right text-slate-900 font-medium tabular-nums">${formatNum(c.monto_mm)}</td>
                          <td className="py-2 text-right text-emerald-700 font-medium tabular-nums">${formatNum(c.vigente_mm)}</td>
                          <td className="py-2 text-right text-slate-600 tabular-nums">{c.pct_vigente}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              {/* Evolución mensual */}
              <SectionCard title="Evolución mensual 2025" subtitle="Monto total y cartera vigente (MM CLP)">
                <AreaChart
                  categories={evoMeses}
                  series={[
                    { name: "Monto total", data: evoMonto },
                    { name: "Vigente 0-30d", data: evoVigente },
                  ]}
                  colors={["#7C3AED", "#10B981"]}
                  height={260}
                />
              </SectionCard>
            </>
          )}

          {/* === CARTERA VIGENTE === */}
          {etapa === "vigente" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vigente 0-30d</div>
                  <div className="mt-2 text-2xl font-bold text-emerald-600 tabular-nums">${formatNum(data.vigente_0_30_mm)}</div>
                  <div className="text-sm text-slate-500">MM CLP</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">% del total</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{data.pct_vigente}%</div>
                  <div className="text-sm text-slate-500">de ${formatNum(data.monto_total_mm)} MM</div>
                </div>
              </div>

              {/* Top convenios por vigente */}
              <SectionCard title="Top convenios por cartera vigente" subtitle="Ordenados por monto vigente">
                <BarChart
                  categories={convenioLabels}
                  series={[{ name: "Vigente MM", data: topConvenios.map((c) => c.vigente_mm) }]}
                  colors={["#10B981"]}
                  height={280}
                />
              </SectionCard>

              {/* Evolución vigente */}
              <SectionCard title="Evolución cartera vigente" subtitle="Vigente 0-30d mensual (MM CLP)">
                <AreaChart
                  categories={evoMeses}
                  series={[{ name: "Vigente 0-30d", data: evoVigente }]}
                  colors={["#10B981"]}
                  height={240}
                />
              </SectionCard>
            </>
          )}

          {/* === MORA TEMPRANA === */}
          {etapa === "mora_temprana" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mora 31-60d</div>
                  <div className="mt-2 text-2xl font-bold text-amber-600 tabular-nums">${formatNum(data.mora_31_60_mm)}</div>
                  <div className="text-sm text-slate-500">MM CLP</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">% del total</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{data.pct_mora_temprana}%</div>
                  <div className="text-sm text-slate-500">de la cartera</div>
                </div>
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                <TrendingDown className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Tendencia positiva</div>
                  <p className="text-sm text-slate-700 mt-1">
                    La mora 31-60 días bajó de <strong className="tabular-nums">$11.905 MM</strong> (Ene) a <strong className="tabular-nums">${formatNum(data.mora_31_60_mm)} MM</strong> (Oct 2025).
                    Reducción sostenida durante 10 meses.
                  </p>
                </div>
              </div>

              {/* Evolución mora */}
              <SectionCard title="Evolución mora 31-60 días" subtitle="Tendencia mensual 2025 (MM CLP)">
                <AreaChart
                  categories={evoMeses}
                  series={[{ name: "Mora 31-60d", data: evoMora }]}
                  colors={["#F59E0B"]}
                  height={240}
                />
              </SectionCard>
            </>
          )}

          {/* === MORA AVANZADA === */}
          {etapa === "mora_avanzada" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mora 61-180d</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">${formatNum(data.mora_61_180_mm)}</div>
                  <div className="text-sm text-slate-500">MM CLP</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mora 181-360d</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">${formatNum(data.mora_181_360_mm)}</div>
                  <div className="text-sm text-slate-500">MM CLP</div>
                </div>
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                <TrendingUp className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Mejora significativa</div>
                  <p className="text-sm text-slate-700 mt-1">
                    La mora avanzada (61-180d) bajó de <strong className="tabular-nums">$37.448 MM</strong> en enero a <strong className="tabular-nums">$0</strong> en octubre 2025.
                    Verificar si corresponde a cobro efectivo o reclasificación contable.
                  </p>
                </div>
              </div>

              {/* Evolución mora avanzada */}
              <SectionCard title="Evolución mora 61-180 días" subtitle="Tendencia mensual 2025 (MM CLP)">
                <AreaChart
                  categories={evoMeses}
                  series={[
                    { name: "Mora 61-180d", data: (data.evolucion_mensual || []).map((e) => {
                      const moraAvz = e.monto_mm - e.vigente_mm - e.mora31_mm;
                      return moraAvz > 0 ? moraAvz : 0;
                    })},
                  ]}
                  colors={["#EF4444"]}
                  height={240}
                />
              </SectionCard>
            </>
          )}

          {/* === RECUPERACIONES === */}
          {etapa === "recuperaciones" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monto total</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">${formatNum(data.monto_total_mm)}</div>
                  <div className="text-sm text-slate-500">MM CLP facturados</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Episodios</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.episodios)}</div>
                  <div className="text-sm text-slate-500">en el período</div>
                </div>
              </div>

              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3">
                <DollarSign className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700">
                  Las recuperaciones se calculan como la diferencia entre facturación y cartera pendiente.
                  Para un análisis detallado de recuperaciones, consultar <code className="text-xs bg-slate-100 px-1 rounded">ar_camadas_castigados</code>.
                </p>
              </div>

              {/* Evolución total */}
              <SectionCard title="Evolución facturación mensual" subtitle="Monto total 2025 (MM CLP)">
                <AreaChart
                  categories={evoMeses}
                  series={[{ name: "Monto total", data: evoMonto }]}
                  colors={["#3B82F6"]}
                  height={240}
                />
              </SectionCard>
            </>
          )}

          {/* Metodología — siempre visible */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <Database className="w-3.5 h-3.5" /> Metodología
            </div>
            <p className="text-sm text-slate-700">
              <strong>Fuente:</strong> <code className="text-xs bg-slate-100 px-1 rounded">ciclo_ingresos.ar_camadas_tot_monto</code> en Redshift.
              Datos disponibles: enero–octubre 2025.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Montos:</strong> En millones de pesos chilenos (MM CLP). Vigente = 0-30 días desde facturación.
              Mora temprana = 31-60 días. Mora avanzada = 61-360+ días.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Convenios:</strong> Top 7 por monto facturado en el mes de referencia.
              Episodios = registros únicos en la camada.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Limitación:</strong> No hay datos de cobranzas para 2026. Los períodos del dashboard
              muestran el último dato disponible con nota explicativa.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
